import { useState, useEffect } from 'react';
import { ArrowLeft, Download, FileText, Calendar, Filter } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { supabase } from '@/integrations/supabase/client';

const ExportCenter = () => {
  const [exportTemplates, setExportTemplates] = useState<any[]>([]);
  const [recentExports, setRecentExports] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState('pdf');
  const [selectedDateRange, setSelectedDateRange] = useState('month');
  const [selectedSites, setSelectedSites] = useState('all');
  const [includeSignatures, setIncludeSignatures] = useState(true);
  const [includeCitations, setIncludeCitations] = useState(true);
  const [includeTimestamps, setIncludeTimestamps] = useState(true);
  const [includePhotos, setIncludePhotos] = useState(false);

  useEffect(() => {
    fetchExportData();
  }, []);

  const fetchExportData = async () => {
    try {
      // Fetch export templates instead of document templates
      const { data: templates } = await supabase
        .from('export_templates')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      // Fetch recent export requests
      const { data: exports } = await supabase
        .from('export_requests')
        .select(`
          *,
          users_public!requested_by(name, email)
        `)
        .order('requested_at', { ascending: false })
        .limit(10);

      setExportTemplates(templates || []);
      setRecentExports(exports || []);
    } catch (error) {
      console.error('Error fetching export data:', error);
      // Set empty arrays instead of using mock data
      setExportTemplates([]);
      setRecentExports([]);
    } finally {
      setLoading(false);
    }
  };

  const generateExport = async (exportType: string, format = selectedFormat) => {
    if (generating) return;
    
    setGenerating(true);
    try {
      // Calculate date range based on selection
      const getDateRange = () => {
        const now = new Date();
        let daysBack = 30;
        switch (selectedDateRange) {
          case 'week': daysBack = 7; break;
          case 'quarter': daysBack = 90; break;
          case 'year': daysBack = 365; break;
          default: daysBack = 30;
        }
        return {
          startDate: new Date(now.getTime() - daysBack * 24 * 60 * 60 * 1000).toISOString(),
          endDate: now.toISOString()
        };
      };

      const dateRange = getDateRange();
      
      const { data, error } = await supabase.functions.invoke('generate-pdf-export', {
        body: {
          exportType,
          format,
          parameters: {
            ...dateRange,
            sites: selectedSites,
            includeSignatures,
            includeCitations,
            includeTimestamps,
            includePhotos
          }
        }
      });

      if (error) throw error;

      // Refresh the exports list
      await fetchExportData();
      
      // Download the file
      if (data.downloadUrl) {
        window.open(data.downloadUrl, '_blank');
      }
    } catch (error) {
      console.error('Error generating export:', error);
    } finally {
      setGenerating(false);
    }
  };

  // Use real data directly - no mock fallback
  const exportTemplatesData = exportTemplates;
  const recentExportsData = recentExports;

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="container mx-auto max-w-6xl">
        <div className="mb-8">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4">
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Link>
          <h1 className="heading-section mb-2">Export Center</h1>
          <p className="text-muted-foreground">Export compliance documents with legal citations and signatures</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Export Templates */}
          <div className="lg:col-span-2">
            <Card className="glass-panel border-0 mb-6">
              <CardHeader>
                <CardTitle>Export Templates</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Loading templates...</p>
                  </div>
                ) : exportTemplatesData.length > 0 ? (
                  exportTemplatesData.map((template) => (
                    <div key={template.id} className="glass-panel p-4 border border-white/10">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="font-medium mb-1">{template.name}</h4>
                          <p className="text-sm text-muted-foreground mb-2">{template.description}</p>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <span>Format: {template.format?.toUpperCase() || 'PDF'}</span>
                            <span>Regions: {Array.isArray(template.regions) ? template.regions.join(', ') : 'All'}</span>
                            <span>Type: {template.export_type.replace('_', ' ')}</span>
                          </div>
                        </div>
                        <Button 
                          size="sm" 
                          className="cta-primary"
                          onClick={() => generateExport(template.export_type, template.format)}
                          disabled={generating}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          {generating ? 'Generating...' : 'Generate'}
                        </Button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No export templates available</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Exports */}
            <Card className="glass-panel border-0">
              <CardHeader>
                <CardTitle>Recent Exports</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Loading exports...</p>
                  </div>
                ) : recentExportsData.length > 0 ? (
                  recentExportsData.map((export_) => (
                    <div key={export_.id} className="flex items-center justify-between p-4 glass-panel border border-white/10">
                      <div className="flex items-center gap-3">
                        <FileText className="h-8 w-8 text-muted-foreground" />
                        <div>
                          <h4 className="font-medium">{export_.export_type.replace('_', ' ').toUpperCase()} ({export_.format})</h4>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <span>{export_.export_type.replace('_', ' ')}</span>
                            <span>{new Date(export_.requested_at).toLocaleString()}</span>
                            <span>{export_.file_size ? `${Math.round(export_.file_size / 1024)} KB` : 'Processing...'}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {export_.status === 'completed' && export_.file_url ? (
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="glass-button"
                            onClick={() => window.open(export_.file_url, '_blank')}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        ) : (
                          <span className="text-sm text-yellow-400">
                            {export_.status === 'processing' ? 'Generating...' : export_.status}
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No recent exports</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Export Configuration */}
          <div>
            <Card className="glass-panel border-0">
              <CardHeader>
                <CardTitle>Export Configuration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Date Range</label>
                  <Select value={selectedDateRange} onValueChange={setSelectedDateRange}>
                    <SelectTrigger className="glass-input">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="week">Last 7 Days</SelectItem>
                      <SelectItem value="month">Last 30 Days</SelectItem>
                      <SelectItem value="quarter">Last 3 Months</SelectItem>
                      <SelectItem value="year">Last Year</SelectItem>
                      <SelectItem value="custom">Custom Range</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Sites</label>
                  <Select value={selectedSites} onValueChange={setSelectedSites}>
                    <SelectTrigger className="glass-input">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Sites</SelectItem>
                      <SelectItem value="downtown">Downtown Restaurant</SelectItem>
                      <SelectItem value="kitchen">Central Kitchen</SelectItem>
                      <SelectItem value="catering">Catering Unit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Export Format</label>
                  <Select value={selectedFormat} onValueChange={setSelectedFormat}>
                    <SelectTrigger className="glass-input">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pdf">PDF Report</SelectItem>
                      <SelectItem value="excel">Excel Spreadsheet</SelectItem>
                      <SelectItem value="csv">CSV Data</SelectItem>
                      <SelectItem value="json">JSON Data</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-3">
                  <label className="text-sm font-medium block">Include</label>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="signatures" 
                        checked={includeSignatures} 
                        onCheckedChange={(checked) => setIncludeSignatures(!!checked)} 
                      />
                      <label htmlFor="signatures" className="text-sm">Digital Signatures</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="citations" 
                        checked={includeCitations} 
                        onCheckedChange={(checked) => setIncludeCitations(!!checked)} 
                      />
                      <label htmlFor="citations" className="text-sm">Legal Citations</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="timestamps" 
                        checked={includeTimestamps} 
                        onCheckedChange={(checked) => setIncludeTimestamps(!!checked)} 
                      />
                      <label htmlFor="timestamps" className="text-sm">Timestamps</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="photos" 
                        checked={includePhotos} 
                        onCheckedChange={(checked) => setIncludePhotos(!!checked)} 
                      />
                      <label htmlFor="photos" className="text-sm">Evidence Photos</label>
                    </div>
                  </div>
                </div>

                <Button 
                  className="w-full cta-primary"
                  onClick={() => generateExport('compliance_report')}
                  disabled={generating}
                >
                  <Download className="h-4 w-4 mr-2" />
                  {generating ? 'Generating...' : 'Generate Custom Export'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportCenter;